import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import EntryDialog from "@/components/EntryDialog";

const MONTHS_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];
const WEEKDAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function buildCalendarGrid(year, month) {
  // month: 0-11. Returns array of {date: YYYY-MM-DD, day, inMonth}
  const first = new Date(year, month, 1);
  const startWeekday = (first.getDay() + 6) % 7; // shift so Monday=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  // previous month padding
  const prevMonthDays = new Date(year, month, 0).getDate();
  for (let i = startWeekday - 1; i >= 0; i--) {
    const d = prevMonthDays - i;
    const dateObj = new Date(year, month - 1, d);
    cells.push({ date: isoDate(dateObj), day: d, inMonth: false });
  }
  // this month
  for (let d = 1; d <= daysInMonth; d++) {
    const dateObj = new Date(year, month, d);
    cells.push({ date: isoDate(dateObj), day: d, inMonth: true });
  }
  // next month padding to complete 6 rows (42)
  let next = 1;
  while (cells.length % 7 !== 0 || cells.length < 35) {
    const dateObj = new Date(year, month + 1, next);
    cells.push({ date: isoDate(dateObj), day: next, inMonth: false });
    next++;
    if (cells.length >= 42) break;
  }
  return cells;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function CalendarPage() {
  const { user } = useAuth();
  const today = new Date();
  const canToggle = user?.is_master_admin || user?.role === "admin" || user?.role === "manager";
  const [view, setView] = useState(user?.role === "lecturer" && !canToggle ? "wb" : "own");
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-11
  const [coachFilter, setCoachFilter] = useState("");
  const [entries, setEntries] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [wbSessions, setWbSessions] = useState([]);
  const [companyGoal, setCompanyGoal] = useState(400);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [presetDate, setPresetDate] = useState(null);

  useEffect(() => {
    api.get("/kpi/company-goals").then((r) => setCompanyGoal(r.data.coaching_ue_target)).catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (view === "wb") {
        const wbRes = await api.get("/weiterbildung/attendance/calendar", { params: { year, month: month + 1 } });
        setWbSessions(wbRes.data || []);
        setEntries([]); setAppointments([]);
      } else {
        const start = `${year}-${(month + 1).toString().padStart(2, "0")}-01`;
        const lastDay = new Date(year, month + 1, 0).getDate();
        const end = `${year}-${(month + 1).toString().padStart(2, "0")}-${lastDay.toString().padStart(2, "0")}`;
        const params = { start_date: start, end_date: end };
        if (coachFilter) params.coach_user_id = coachFilter;
        const apptParams = { from_date: start, to_date: end };
        if (coachFilter) apptParams.coach_user_id = coachFilter;
        const [eRes, cRes, aRes] = await Promise.all([
          api.get("/entries", { params }),
          api.get("/coaches"),
          api.get("/appointments", { params: apptParams }),
        ]);
        setEntries(eRes.data);
        setCoaches(cRes.data.filter((c) => c.role !== "admin"));
        setAppointments(aRes.data || []);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, coachFilter, view]);

  const cells = useMemo(() => buildCalendarGrid(year, month), [year, month]);

  const aggByDate = useMemo(() => {
    const map = {};
    for (const e of entries) {
      if (!map[e.date]) map[e.date] = { hours: 0, ue: 0, customers: 0, items: [] };
      map[e.date].hours += parseFloat(e.hours || 0);
      map[e.date].ue += parseFloat(e.ue || 0);
      map[e.date].customers += parseInt(e.customers || 0, 10);
      map[e.date].items.push(e);
    }
    return map;
  }, [entries]);

  const apptsByDate = useMemo(() => {
    const map = {};
    for (const a of appointments) {
      if (a.status === "rejected") continue;
      if (!map[a.date]) map[a.date] = [];
      map[a.date].push(a);
    }
    return map;
  }, [appointments]);

  const wbByDate = useMemo(() => {
    const map = {};
    for (const s of wbSessions) {
      if (!map[s.date]) map[s.date] = [];
      map[s.date].push(s);
    }
    return map;
  }, [wbSessions]);

  const totals = useMemo(() => {
    const fromEntries = entries.reduce(
      (acc, e) => {
        acc.hours += parseFloat(e.hours || 0);
        acc.ue += parseFloat(e.ue || 0);
        acc.customers += parseInt(e.customers || 0, 10);
        return acc;
      },
      { hours: 0, ue: 0, customers: 0 }
    );
    // Unify with /stats/monthly, /kpi/team-celebration: coaching UE = manuelle Einträge
    // + unterschriebene (signed) Termine. Sonst driftet die Kalender-Summe von Dashboard/Team-Erfolg.
    for (const a of appointments) {
      if (a.status !== "signed") continue;
      fromEntries.hours += parseFloat(a.planned_hours || 0);
      fromEntries.ue += parseFloat(a.planned_ue || 0);
    }
    return fromEntries;
  }, [entries, appointments]);

  const goPrev = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else setMonth(month - 1);
  };
  const goNext = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else setMonth(month + 1);
  };
  const goToday = () => {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  };

  const handleCellClick = (cell) => {
    setPresetDate(cell.date);
    setDialogOpen(true);
  };

  const todayIso = isoDate(today);
  const progress = Math.min(100, (totals.ue / companyGoal) * 100);

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1400px] mx-auto" data-testid="calendar-root">
      <div className="flex items-end justify-between mb-10 border-b border-slate-200 pb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Monatsplan</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">
            {MONTHS_DE[month]} {year}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {(user?.all_roles || [user?.role]).some((r) => ["admin", "manager", "coach"].includes(r)) && (
            <a href="/terminplanung?auto=1" data-testid="cal-autoplan-btn"
              className="px-4 h-10 bg-slate-900 text-white text-sm hover:bg-black flex items-center">AVGS-Termine automatisch planen</a>
          )}
          <button
            data-testid="cal-prev"
            onClick={goPrev}
            className="w-10 h-10 border border-slate-300 flex items-center justify-center hover:bg-slate-100"
            aria-label="Vorheriger Monat"
          >
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <button
            data-testid="cal-today"
            onClick={goToday}
            className="px-4 h-10 border border-slate-300 text-sm hover:bg-slate-100"
          >
            Heute
          </button>
          <button
            data-testid="cal-next"
            onClick={goNext}
            className="w-10 h-10 border border-slate-300 flex items-center justify-center hover:bg-slate-100"
            aria-label="Nächster Monat"
          >
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
          <select
            data-testid="cal-coach-filter"
            className={`input-tech max-w-[220px] !py-2 border border-slate-300 border-b-slate-300 px-3 ${(view === "own" && user?.role === "admin") ? "" : "hidden"}`}
            value={coachFilter}
            onChange={(e) => setCoachFilter(e.target.value)}
          >
            <option value="">Alle Coaches</option>
            {coaches.map((c) => (
              <option key={c.user_id} value={c.user_id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {canToggle && (
        <div className="flex gap-2 mb-6" data-testid="calendar-view-toggle">
          <button
            data-testid="calendar-view-own"
            onClick={() => setView("own")}
            className={`px-4 py-2 text-sm font-medium border ${view === "own" ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}
          >
            Meine Kalender
          </button>
          <button
            data-testid="calendar-view-wb"
            onClick={() => setView("wb")}
            className={`px-4 py-2 text-sm font-medium border ${view === "wb" ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}
          >
            Weiterbildungskalender
          </button>
        </div>
      )}

      {/* Month summary strip */}
      {view === "own" ? (
      <div className="grid grid-cols-1 md:grid-cols-4 border border-slate-200 bg-white mb-6">
        <SummaryCell
          label={user?.role === "admin" ? "UE Monat" : "Meine UE"}
          value={totals.ue.toFixed(totals.ue % 1 === 0 ? 0 : 1)}
          suffix={user?.role === "admin" ? `/ ${companyGoal}` : ""}
          accent
        />
        <SummaryCell
          label={user?.role === "admin" ? "Fortschritt" : "Anteil am Team-Ziel"}
          value={`${progress.toFixed(1)}%`}
        />
        <SummaryCell label="Stunden" value={totals.hours.toFixed(totals.hours % 1 === 0 ? 0 : 1)} suffix="h" />
        <SummaryCell label="Kunden" value={totals.customers} border={false} />
      </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 border border-slate-200 bg-white mb-6">
        <SummaryCell label="Termine im Monat" value={wbSessions.length} accent />
        <SummaryCell label="Teilnehmer gesamt" value={wbSessions.reduce((s, w) => s + w.participant_count, 0)} border={false} />
      </div>
      )}

      <div className="border border-slate-200 bg-white">
        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-slate-200">
          {WEEKDAYS_DE.map((w) => (
            <div
              key={w}
              className="px-3 py-3 text-xs font-bold uppercase tracking-overline text-slate-500 border-r border-slate-100 last:border-r-0"
            >
              {w}
            </div>
          ))}
        </div>

        {/* Cells */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-7"
        >
          {cells.map((cell, idx) => {
            const agg = aggByDate[cell.date];
            const dayAppts = apptsByDate[cell.date] || [];
            const daySessions = wbByDate[cell.date] || [];
            const isToday = cell.date === todayIso;
            const hasEntries = !!agg;
            const hasAppts = dayAppts.length > 0;
            const hasSessions = daySessions.length > 0;
            return (
              <button
                type="button"
                key={cell.date + idx}
                data-testid={`cal-cell-${cell.date}`}
                onClick={() => view === "own" && handleCellClick(cell)}
                className={`relative text-left p-3 min-h-[110px] border-b border-r border-slate-100 transition-colors ${
                  cell.inMonth ? "bg-white" : "bg-slate-50"
                } hover:bg-slate-100 ${(idx + 1) % 7 === 0 ? "border-r-0" : ""}`}
              >
                <div className="flex items-baseline justify-between">
                  <span
                    className={`font-mono text-sm ${
                      cell.inMonth ? (isToday ? "text-blue-700 font-bold" : "text-slate-900") : "text-slate-400"
                    }`}
                  >
                    {cell.day.toString().padStart(2, "0")}
                  </span>
                  {isToday && (
                    <span className="text-[9px] font-bold uppercase tracking-overline text-blue-700">Heute</span>
                  )}
                </div>

                {view === "wb" && hasSessions && (
                  <div className="mt-2 space-y-1" data-testid={`cal-wb-${cell.date}`}>
                    {daySessions.slice(0, 3).map((s, i) => (
                      <div key={i} className="text-[10px] px-1.5 py-1 bg-[#e6f3f6] border border-[#0a788c] text-[#0a5568] truncate">
                        {s.class_name} · {s.participant_count} TN
                      </div>
                    ))}
                  </div>
                )}

                {view === "own" && hasEntries && (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-overline text-slate-500">UE</span>
                      <span className="font-mono text-sm text-blue-700 font-medium">
                        {agg.ue.toFixed(agg.ue % 1 === 0 ? 0 : 1)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-overline text-slate-500">h</span>
                      <span className="font-mono text-xs text-slate-700">
                        {agg.hours.toFixed(agg.hours % 1 === 0 ? 0 : 1)}
                      </span>
                    </div>
                    {agg.customers > 0 && (
                      <div className="text-[10px] font-mono text-slate-500">
                        {agg.customers} {agg.customers === 1 ? "Kunde" : "Kunden"}
                      </div>
                    )}
                  </div>
                )}

                {view === "own" && !hasEntries && cell.inMonth && (
                  <span className="absolute bottom-2 right-2 text-slate-300 opacity-0 group-hover:opacity-100">
                    <Plus size={12} strokeWidth={1.5} />
                  </span>
                )}

                {view === "own" && hasAppts && (
                  <div className="mt-2 space-y-0.5" data-testid={`cal-appts-${cell.date}`}>
                    {dayAppts.slice(0, 3).map((a) => {
                      const statusCls = {
                        proposed: "bg-amber-50 border-amber-300 text-amber-900",
                        accepted: "bg-[#e6f3f6] border-[#0a788c] text-[#0a5568]",
                        completed: "bg-slate-100 border-slate-400 text-slate-800",
                        signed: "bg-emerald-50 border-emerald-400 text-emerald-800",
                      }[a.status] || "bg-slate-50 border-slate-300 text-slate-700";
                      return (
                        <div
                          key={a.appointment_id}
                          className={`text-[10px] leading-tight border-l-2 px-1.5 py-0.5 font-mono truncate ${statusCls}`}
                          title={`${a.time_from || ""}–${a.time_to || ""} · ${a.client_name} · ${a.planned_ue} UE`}
                        >
                          {a.time_from ? `${a.time_from} ` : ""}
                          {a.client_name}
                        </div>
                      );
                    })}
                    {dayAppts.length > 3 && (
                      <div className="text-[10px] text-slate-500 font-mono">+{dayAppts.length - 3} weitere</div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </motion.div>
      </div>

      {loading && (
        <div className="mt-4 text-xs font-bold uppercase tracking-overline text-slate-500">Lädt Einträge…</div>
      )}

      <EntryDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setPresetDate(null);
        }}
        coachUserId={user?.user_id}
        coaches={coaches}
        isAdmin={user?.role === "admin"}
        currentUserId={user?.user_id}
        onSaved={fetchData}
        initialData={presetDate ? { date: presetDate, coach_user_id: user?.user_id } : null}
      />
    </div>
  );
}

function SummaryCell({ label, value, suffix, border = true, accent = false }) {
  return (
    <div className={`p-6 ${border ? "border-r border-slate-200" : ""}`}>
      <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2">{label}</div>
      <div
        className={`font-heading text-3xl font-light tracking-tighter ${accent ? "text-blue-700" : "text-slate-900"}`}
      >
        {value}
        {suffix && <span className="text-slate-400 text-xl ml-1">{suffix}</span>}
      </div>
    </div>
  );
}
